import React, { useEffect, useState, useCallback } from "react";
import InitDisplay from "./InitSupportDisplay";
import NoSupportDisplay from "./NoSupportDisplay";
import { Conversation, getConversations } from "./utils/conversations";
import { ChatItem } from "react-chat-elements";
import { FiSearch } from "react-icons/fi";
import "react-chat-elements/dist/main.css";
import "tailwindcss/tailwind.css";
import ChatContainer from "./utils/messageView";
import Logo from "assets/img/LOGO.png";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import ContactSupportPopPup from "./ContactSupportPopPup";
import { markMessagesRead } from "server/Conversations CRUD/Requests";
import { useNavigate } from "react-router-dom";

// Style CSS personnalisé pour forcer la couleur des badges
const customStyles = `
  .rce-citem__unread {
    background-color: #131CC9 !important;
    color: white !important;
  }
`;

const defaultAvatarUrl = "assets/img/logoJaune.png";

interface AssistanceProps {
  userRole?: string;
}

const Assistance: React.FC<AssistanceProps> = ({ userRole = "student" }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [converSmView, setconverSmView] = useState<string>("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationTitle, setConversationTitle] = useState<string>("");
  const [chatSmView, setchatSmView] = useState<string>("hidden sm:block");
  const [showSendMessagePopup, setshowSendMessagePopup] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showDefaultPage, setShowDefaultPage] = useState<boolean>(true);
  const navigate = useNavigate();

  // Déterminer si l'utilisateur est un administrateur
  const isAdmin = userRole === "adminStartup" || userRole === "adminSchool";

  // Texte adapté selon le rôle
  const getActionText = () => {
    if (isAdmin) {
      return "Créer ma première conversation";
    }
    return "Contacter l'administration";
  };

  // Fonction pour charger les conversations
  const fetchConversations = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) {
        setIsLoading(true);
      }
      const convs = await getConversations("SupportContact");
      setConversations(convs);

      // Si il y a des conversations, masquer la page par défaut
      if (convs.length > 0) {
        setShowDefaultPage(false);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des conversations:", error);
    } finally {
      if (showLoader) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Rafraîchir les conversations périodiquement pour mettre à jour les messages non lus
  useEffect(() => {
    const interval = setInterval(() => {
      fetchConversations(false); // Pas de loader pour le rafraîchissement automatique
    }, 30000); // Rafraîchir toutes les 30 secondes

    return () => clearInterval(interval);
  }, [fetchConversations]);

  // Fonction pour marquer les messages comme lus
  const handleMarkMessagesRead = useCallback(
    async (conversationId: string) => {
      try {
        // Mise à jour optimiste de l'interface
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId ? { ...conv, unread: 0 } : conv,
          ),
        );

        // Appel à l'API pour marquer comme lus
        const response = await markMessagesRead({ conversationId });

        if (response.status !== 200) {
          console.error(
            "Erreur lors du marquage des messages comme lus:",
            response.body,
          );
          // En cas d'erreur, recharger les conversations pour avoir l'état correct
          fetchConversations(false); // Pas de loader pour le rechargement d'erreur
        }
      } catch (error) {
        console.error("Erreur lors du marquage des messages comme lus:", error);
        // En cas d'erreur, recharger les conversations pour avoir l'état correct
        fetchConversations(false); // Pas de loader pour le rechargement d'erreur
      }
    },
    [fetchConversations],
  );

  // Fonction pour gérer le clic sur une conversation
  const handleConversationClick = useCallback(
    (conversation: Conversation) => {
      setConversationTitle(conversation.title);
      setConversationId(conversation.id);
      setconverSmView("hidden sm:flex");
      setchatSmView("w-screen sm:w-full");

      // Marquer les messages comme lus si il y en a
      if (conversation.unread && conversation.unread > 0) {
        handleMarkMessagesRead(conversation.id);
      }
    },
    [handleMarkMessagesRead],
  );

  const handleConversationCreated = (newConversation: {
    id: string;
    title: string;
  }) => {
    // Ajouter la nouvelle conversation à la liste sans rechargement
    const newConversationItem: Conversation = {
      id: newConversation.id,
      title: newConversation.title,
      position: "left",
      date: new Date(),
      avatar: defaultAvatarUrl,
      unread: 0,
    };

    setConversations((prev) => [newConversationItem, ...prev]);

    // Masquer la page par défaut et afficher la liste des conversations
    setShowDefaultPage(false);
    setconverSmView("");
    setchatSmView("hidden sm:flex");
    setshowSendMessagePopup(false);
  };

  // Filtrer les conversations basées sur la recherche
  const filteredConversations = conversations.filter((conversation) =>
    conversation.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleBackToConversations = () => {
    setconverSmView("");
    setchatSmView("hidden sm:flex");
    setConversationId(null);
    setConversationTitle("");
  };

  return (
    <div className="h-[650px] w-full">
      <style>{customStyles}</style>

      {/* Page par défaut - affichée quand il n'y a pas de conversations */}
      {showDefaultPage && !isLoading && conversations.length === 0 ? (
        <div className="h-full bg-white rounded-lg shadow-sm flex items-center justify-center">
          <div className="text-center">
            <div className="w-32 h-32 mx-auto mb-6">
              <img
                src={Logo}
                alt="Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Bienvenue au Support
            </h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Rencontrez-vous un problème avec la plateforme ? Contacter
              l'administraction de la plateforme pour recevoir
              rapidement de l'aide
            </p>
            {userRole !== "adminStartup" && (
              <button
                onClick={() => setshowSendMessagePopup(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto"
              >
                <PlusCircleIcon className="w-5 h-5" />
                {getActionText()}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex h-full bg-white rounded-lg shadow-sm">
          {/* Sidebar des conversations */}
          <div
            className={`w-full sm:w-80 border-r border-gray-200 flex flex-col h-full max-h-full ${converSmView}`}
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">Support</h2>
                {userRole !== "adminStartup" && (
                  <button
                    onClick={() => setshowSendMessagePopup(true)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                  >
                    <PlusCircleIcon className="w-6 h-6" />
                  </button>
                )}
              </div>

              {/* Barre de recherche */}
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher une conversation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Liste des conversations */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <p className="text-center">
                    {searchQuery
                      ? "Aucune conversation trouvée"
                      : "Aucune conversation"}
                  </p>
                </div>
              ) : (
                filteredConversations.map((conversation) => (
                  <ChatItem
                    key={conversation.id}
                    id={conversation.id}
                    avatar={Logo}
                    alt={conversation.title}
                    title={conversation.title}
                    subtitle={conversation.text || "Aucun message"}
                    date={conversation.date}
                    unread={conversation.unread || 0}
                    onClick={() => handleConversationClick(conversation)}
                    className="cursor-pointer hover:bg-gray-50 transition-colors [&_.rce-citem__unread]:!bg-[#131CC9] [&_.rce-citem__unread]:!text-white [&_.rce-citem__unread]:!important"
                  />
                ))
              )}
            </div>
          </div>

          {/* Zone de chat */}
          <div className={`flex-1 flex flex-col h-full ${chatSmView}`}>
            {conversationId ? (
              <ChatContainer
                conversationId={conversationId}
                conversationTitle={conversationTitle}
                onBack={handleBackToConversations}
                userRole={userRole}
              />
            ) : conversations.length === 0 &&
              converSmView === "hidden sm:flex" ? (
              <NoSupportDisplay
                onNewConversation={() => setshowSendMessagePopup(true)}
                userRole={userRole}
              />
            ) : (
              <InitDisplay
                onNewConversation={() => setshowSendMessagePopup(true)}
                userRole={userRole}
              />
            )}
          </div>
        </div>
      )}

      {/* Popup pour créer une nouvelle conversation */}
      {showSendMessagePopup && (
        <ContactSupportPopPup
          onClose={() => setshowSendMessagePopup(false)}
          onConversationCreated={handleConversationCreated}
          userRole={userRole}
        />
      )}
    </div>
  );
};

export default Assistance;
